import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "dvajs";
import { GroupSettingItem, SettingSwitch } from "@/components/chat/SessionSetting/GroupManager";
import IconFont from "@/components/icon";
import { Drawer, message, Button, Checkbox } from "antd";
import { List, ListItem } from "@jd/focus-desktop-comps";
import { getUserAlias } from "@/utils/chat/group";
import { changeEgovUrl } from "@/utils/tools";
import { CrossTag } from "@/components/Tags";
import dayjs from "dayjs";
import { CompDataLoadState, DefaultPageType } from "@/models";
import DefaultPage from "@/components/DefaultPage";
import { openUserSelectionModal } from "@/utils/modals";
import { employeeToUid, isGroupVisibleUnit } from "@/utils/chat/message";
import {
  SelectorMode,
  ContactShowType,
  SelectGroupType
} from "@jd/focus-desktop-comps/lib/es/UserSelection";
import { getFixedUsers } from "../GroupOperation";
import { Session, GroupRoster } from "@/types/chat";
import ChatState from "@/types/chat/State";
import Context from "@/components/chat/context";
import { setSessionAttention } from "@/server/im/SessionService";
import ImService from "@/server/ImService";
import GlobalContext from "@/context/GlobalContext";

interface IProps {
  selectedSession: Session;
}

const ConcernedGroupMember = ({ selectedSession }: IProps) => {
  const { t } = useTranslation("chat");
  const [visible, setVisible] = useState(false);
  const [removable, setRemovable] = useState(false);
  const [attentionMembers, setAttentionMembers] = useState<any[]>([]);
  const [selectedList, setSelectedList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const { appRuntimeEnv } = React.useContext(GlobalContext);
  const { currentEmployee, groupRosters, userData } = React.useContext(Context);

  useEffect(() => {
    const imService = ImService.getInstance();
    const { attention = [] }: any = selectedSession.settings;
    const _attention = attention.filter((item: { app: string; teamId: string; pin: string }) => {
      const { teamId, pin, app } = item;
      const roster = groupRosters.find((roster: any) => {
        return roster.user.teamId === teamId && roster.user.app === app && roster.user.pin === pin;
      });
      if (!roster) {
        return false;
      }
      return true;
    });
    imService
      .getChangeUserDetailList(
        _attention.map((item: { app: string; teamId: string; pin: string }) => ({
          appId: item.app,
          teamId: item.teamId,
          userId: item.pin
        }))
      )
      .then((res: any) => {
        setAttentionMembers(
          res.map((item: any) => ({
            ...item,
            id: `${item.userId}:${item.app}:${item.teamId}`
          }))
        );
      });
  }, [selectedSession, groupRosters]);

  const onClose = useCallback(() => {
    setVisible(false);
    setRemovable(false);
    setSelectedList([]);
  }, []);

  const titleNode = useMemo(() => {
    return (
      <div className="header">
        <div>
          <IconFont
            type="iconwin_left"
            className="group-management-icon-arrow app-region-none"
            onClick={onClose}
          />
          <span className="drawer-title">{t("concerned_group_members")}</span>
        </div>
        {!!attentionMembers.length && (
          <Button
            className="remove-btn app-region-none"
            type="link"
            onClick={() => {
              setRemovable(!removable);
              setSelectedList([]);
            }}
          >
            {removable ? "取消" : "移除"}
          </Button>
        )}
      </div>
    );
  }, [onClose, t, removable, attentionMembers.length]);

  const handleRemoveConfirm = useCallback(async () => {
    const attention = attentionMembers.filter((member) => !selectedList.includes(member.userId));
    setLoading(true);
    const params = attention.map((item) => ({
      pin: item.userId,
      app: item.app,
      teamId: item.teamId || ""
    }));
    const result = await setSessionAttention(selectedSession.sessionId, params);
    if (result) {
      setLoading(false);
      setRemovable(false);
      setSelectedList([]);
    }
  }, [attentionMembers, selectedList]);

  const showUserSelection = useCallback(() => {
    if (!currentEmployee || !userData) {
      return null;
    }
    const isShowUnit = !isGroupVisibleUnit(selectedSession);
    const fixedUsers = getFixedUsers(currentEmployee, groupRosters, isShowUnit, selectedSession);
    openUserSelectionModal(
      {
        title: t("concerned_group_members"),
        frozenIds: [],
        resultUsers: attentionMembers,
        fixedUsers: fixedUsers,
        searchPlaceHolder: "搜索联系人",
        currentUser: employeeToUid(currentEmployee),
        mode: SelectorMode.group,
        selectedCountText: "已选",
        maxSelectedNum: appRuntimeEnv.im.maxAttentionNum || 20,
        notLoad: false
      },
      async (data, close) => {
        const attention: { pin: string; app: string; teamId: string }[] = data.data.result.map(
          (item) => ({
            pin: item.userId,
            app: item.app,
            teamId: item.teamId || ""
          })
        );
        const result = await setSessionAttention(selectedSession.sessionId, attention);
        close();
      },
      userData
    );
  }, [currentEmployee, userData, attentionMembers, selectedSession]);

  const footerNode = useMemo(() => {
    return removable ? (
      <div className="footer">
        <span>
          已选 <i className="num">{selectedList.length}</i>{" "}
        </span>
        <Button type="primary" onClick={handleRemoveConfirm} loading={loading}>
          确认移除
        </Button>
      </div>
    ) : (
      <Button type="primary" onClick={showUserSelection}>
        {attentionMembers.length ? "编辑关注" : "添加关注"}
      </Button>
    );
  }, [
    removable,
    selectedList,
    showUserSelection,
    handleRemoveConfirm,
    loading,
    attentionMembers.length
  ]);

  const handleCheckboxChange = (value: any[]) => {
    setSelectedList(value);
  };

  return (
    <>
      <GroupSettingItem
        title={t("concerned_group_members")}
        onClick={() => {
          setVisible(true);
        }}
        clstag="pageclick|keycount|Xtbg_Msg_Setting|ConcernedGroupMember"
      >
        <IconFont
          type="iconFrame6"
          className="group-management-icon-arrow"
          onClick={() => {
            setVisible(true);
          }}
          clstag="pageclick|keycount|Xtbg_Msg_Setting|ConcernedGroupMember"
        />
      </GroupSettingItem>
      <Drawer
        title={titleNode}
        visible={visible}
        placement="right"
        closable={false}
        onClose={onClose}
        width={310}
        className="group-members-drawer"
        footer={footerNode}
      >
        <div className="notice-container">
          <p className="notice-text">
            添加关注的群后，该成员在群内发消息会提醒您关注，不受免打扰的影响。
          </p>
          <p className="notice-text">
            最多支持添加{appRuntimeEnv.im.maxAttentionNum || 20}名群成员。
          </p>
        </div>
        <div className="attention-members">
          {!!attentionMembers.length && (
            <Checkbox.Group
              onChange={(value) => {
                handleCheckboxChange(value);
              }}
            >
              <List
                dataSource={attentionMembers || []}
                renderItem={(item) => {
                  const alias = getUserAlias(
                    { ...item, app: item.app || (window as any)?._BTS_CONFIG?.REACT_APP_DD_APPID || process.env.REACT_APP_DD_APPID || "gwork" },
                    selectedSession
                  );
                  const isExternal =
                    item.userType === "1" || item.userType === "4" || item.userType === "5";
                  const nameStr = alias ? `${item.name}（${alias}）` : `${item.name}`;
                  let newItem = {
                    ...item,
                    name: nameStr,
                    desc: item.unitName,
                    ...{
                      avatar: changeEgovUrl(item.avatar),
                      externalTag: isExternal && <CrossTag />
                    }
                  };
                  return (
                    <div className="attention-member">
                      {removable && (
                        <Checkbox
                          value={item.userId || item.pin}
                          className="attention-member-checkbox"
                        />
                      )}
                      <ListItem
                        data={newItem}
                        className="attention-member-item"
                        tag={isExternal && <CrossTag />}
                      />
                    </div>
                  );
                }}
              />
            </Checkbox.Group>
          )}
        </div>
      </Drawer>
    </>
  );
};

function mapStateToProps({ chat }: { chat: ChatState }) {
  return {
    selectedSession: chat.selectedSession
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    updateSelectedSession(data: { selectedSession: Session }) {
      dispatch({ type: "chat/updateSelectedSession", payload: data });
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ConcernedGroupMember);
